/**
 * The view cube: a small cube in the 3D view's bottom-left corner that turns
 * exactly as the camera turns, labeled on its six faces in the house's own
 * terms, so the view always shows which way it looks. Pressing a face, an
 * edge or a corner glides the camera to look from that side
 * (`viewCubeAngle`, through `View3DCore.viewFrom`); dragging on it turns the
 * view as if the cube itself were grabbed and turned. Three buttons sit
 * around it: the house in a circle at the area's lower right glides home
 * (`View3DCore.goHome`), and the arrows at its top left and top right spin
 * the house a quarter turn about the vertical (`View3DCore.turnView`), never
 * tipping the floor plan. Pure geometry with no GL: both platforms draw the
 * same cube from `viewCubeLayout`, each with its own SVG, and hit it with
 * `viewCubeRegionAt`. Presses, drags and taps on it are in `viewCubeDragLib`.
 *
 * The cube's axes are the house's, the same front, back, left and right as
 * the room editor's walls: +x toward the right edge (plan x = width), +y up,
 * +z toward the front edge (plan y = length; world z is plan y). So Front
 * looks from in front of the front wall toward the back, and Top is the plan
 * view, the front edge at the bottom.
 *
 * Each face divides into its middle, a band along each of its four sides
 * (half of an edge's region, the other half on the neighboring face) and a
 * square at each corner (a third of a corner's region): 26 regions in all.
 * The cube draws in mild perspective, as the view beside it does, from an
 * eye `EYE_DISTANCE` sides from its center.
 */
import type { Point } from '@kaiizen/kai-floor-plan';
import {
  hexFromRgb,
  HOME_ICON,
  type IconPaths,
  type InputKind,
  PALETTE,
  TOUCH_TARGET_REACH,
  TURN_LEFT_ICON,
  TURN_RIGHT_ICON,
  withinTouchTarget,
} from '../editor-model';
import { add, dot3d, scale, sub, type Vec3 } from '../math3d';
import { VIEW_ELEVATION_LIMIT } from './camera3d';
import { evenOddContains } from './pick3d';
import { sceneLightAt } from './sceneLighting';
import type { CameraAngle } from './View3DCore';

type VerticalFace = 'top' | 'bottom';
type DepthFace = 'front' | 'back';
type SideFace = 'left' | 'right';
export type ViewCubeFace = VerticalFace | DepthFace | SideFace;
/** An edge, named by its two faces: the top or bottom first, then the front or back, then the left or right. */
export type ViewCubeEdge = `${VerticalFace}-${DepthFace}` | `${VerticalFace}-${SideFace}` | `${DepthFace}-${SideFace}`;
/** A corner, named by its three faces in the same order. */
export type ViewCubeCorner = `${VerticalFace}-${DepthFace}-${SideFace}`;
export type ViewCubeRegion = ViewCubeFace | ViewCubeEdge | ViewCubeCorner;
/** The buttons around the cube: spin left at its top left, spin right at its top right, and home at its lower right. */
export type ViewCubeButton = 'turn-left' | 'turn-right' | 'home';
/** What a press on the cube's area takes: a region of the cube, or a button. */
export type ViewCubeHit = ViewCubeRegion | ViewCubeButton;

/** An SVG `matrix(a b c d e f)`: x' = a·u + c·v + e, y' = b·u + d·v + f. */
export type ViewCubeMatrix = readonly [a: number, b: number, c: number, d: number, e: number, f: number];

/** A box in the cube area's coordinates. */
export interface ViewCubeBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** One face as seen: drawn back to front, so the platforms paint them in order. */
export interface ViewCubeFaceShape {
  face: ViewCubeFace;
  label: string;
  /** Its four corners on screen, clockwise. */
  polygon: Point[];
  /**
   * The label's place on the face: the perspective projection as it is at
   * the face's middle, an affine map from the face's unit square (u right
   * and v down as its label reads) that takes (0.5, 0.5) exactly onto the
   * projected middle, with the projection's own scale and skew there. A
   * platform draws the label centered at (0.5, 0.5) at
   * `VIEW_CUBE_STYLE.labelFontSize` inside `transform="matrix(...)"`, so it
   * lies on the face, never mirrored, and reads upright seen head-on.
   */
  matrix: ViewCubeMatrix;
  /** Its color, the cube's under the scene light as the camera sees this face, `#rrggbb`. */
  fill: string;
  /** How far its middle is from the eye, in cube sides: the faces list farthest first. */
  eyeDistance: number;
}

/** One region as seen: what lights up on hover or press, and where a touch aims. */
export interface ViewCubeRegionShape {
  region: ViewCubeRegion;
  /** Its outline on screen, across every face it shows on: fill or stroke it with `VIEW_CUBE_STYLE.highlight`. */
  outline: Point[];
  /** Its visual center: a face's middle, an edge's middle, or the corner itself. */
  center: Point;
}

/**
 * A button around the cube: a `VIEW_CUBE_STYLE.buttonFill` disc outlined in
 * `VIEW_CUBE_STYLE.ink` at `VIEW_CUBE_STYLE.strokeWidth`, the disc being the
 * button's circle, and its bare glyph `icon` drawn in `box` at
 * `iconStrokeWidth(box.width)`, as light as the menu icons. Under touch it
 * takes a press anywhere in the touch target square about its center.
 */
export interface ViewCubeButtonShape {
  button: ViewCubeButton;
  icon: IconPaths;
  center: Point;
  radius: number;
  box: ViewCubeBox;
  /** Its circle, for the hover and press highlight. */
  outline: Point[];
}

/** The cube as the camera sees it, in the cube area's coordinates: (0, 0) at its top left, y down. */
export interface ViewCubeLayout {
  inputKind: InputKind;
  /** The cube area's size: the cube's box with the buttons at its top left, top right and lower right. */
  width: number;
  height: number;
  /** The cube's side on screen. */
  side: number;
  /** The box the cube turns within, whatever the angle, centered on the cube. */
  cubeBox: ViewCubeBox;
  /** The faces that show, back to front. */
  faces: ViewCubeFaceShape[];
  /** Every region that shows on a face that shows. */
  regions: ViewCubeRegionShape[];
  /** The cube's outline on screen. */
  silhouette: Point[];
  /** The three buttons, one loop's worth for a platform to draw. */
  buttons: Record<ViewCubeButton, ViewCubeButtonShape>;
}

/** How both platforms draw the cube: the scene's ink for its edges, its labels and the buttons' outlines and glyphs, and the one highlight for hover and press. */
export const VIEW_CUBE_STYLE = {
  ink: hexFromRgb(PALETTE.outline),
  strokeWidth: 1,
  /** In the face's unit square (see `ViewCubeFaceShape.matrix`). */
  labelFontSize: 0.2,
  labelFontWeight: '600',
  /** Each button's disc. */
  buttonFill: hexFromRgb(PALETTE.ceiling),
  /** The hovered or pressed region: the selection blue, partly see-through, over the faces. */
  highlight: { fill: hexFromRgb(PALETTE.accent), opacity: 0.35 },
} as const;

/** How wide each edge band and corner square is, in cube sides: narrow enough that the edges and corners read apart from the faces they border. */
const BAND = 0.2;
/** How far the eye stands from the cube's center, in cube sides: a gentle perspective, a face's near edge at most about a fifth longer than its far edge. */
const EYE_DISTANCE = 4;
/** How far the eye must stand off a face's plane, on its outward side, for the face to show, in cube sides: nearer, it is edge-on. */
const LEAST_FACING = 0.01;
/** Half the cube's diagonal, in cube sides: how far its corners stand from its center. */
const CORNER_REACH = Math.sqrt(3) / 2;
/** The farthest a corner ever lands from the cube's middle on screen, in cube sides: where a corner's sight line grazes the circle the corners turn on. */
const SCREEN_REACH = (EYE_DISTANCE * CORNER_REACH) / Math.sqrt(EYE_DISTANCE ** 2 - CORNER_REACH ** 2);
/** How high an edge between a side and the top looks from, and a corner: straight 45s to start. Bottom's look from as far below. */
const EDGE_ELEVATION = Math.PI / 4;
const CORNER_ELEVATION = Math.PI / 4;
/** A button's disc across, per input: a little bigger under a finger, whose touch target is the square about its center all the same. */
const BUTTON_SIZE: Record<InputKind, number> = { pointer: 24, touch: 30 };
/** The space between each button and the circle the cube turns within. */
const BUTTON_GAP = 4;
/** A pixel of room past the outermost outlines, where antialiasing paints just beyond the exact stroke. */
const EDGE_PIXEL = 1;
/** A button's circle as a polygon, for its highlight. */
const BUTTON_OUTLINE_SEGMENTS = 32;
const BUTTON_ICONS: Record<ViewCubeButton, IconPaths> = { 'turn-left': TURN_LEFT_ICON, 'turn-right': TURN_RIGHT_ICON, home: HOME_ICON };
/** Whether `hit` is one of the buttons around the cube rather than a region of it. */
export const isViewCubeButton = (hit: ViewCubeHit): hit is ViewCubeButton => hit in BUTTON_ICONS;

/** A face's frame: its outward normal, and its label's right and up. */
interface FaceFrame {
  label: string;
  normal: Vec3;
  right: Vec3;
  up: Vec3;
}

/** Each face's frame, so every label reads upright seen head-on; Top's and Bottom's read as the plan does, the front at the bottom of Top. */
const FACE_FRAMES: Record<ViewCubeFace, FaceFrame> = {
  top: { label: 'Top', normal: [0, 1, 0], right: [1, 0, 0], up: [0, 0, -1] },
  bottom: { label: 'Bottom', normal: [0, -1, 0], right: [1, 0, 0], up: [0, 0, 1] },
  front: { label: 'Front', normal: [0, 0, 1], right: [1, 0, 0], up: [0, 1, 0] },
  back: { label: 'Back', normal: [0, 0, -1], right: [-1, 0, 0], up: [0, 1, 0] },
  left: { label: 'Left', normal: [-1, 0, 0], right: [0, 0, 1], up: [0, 1, 0] },
  right: { label: 'Right', normal: [1, 0, 0], right: [0, 0, -1], up: [0, 1, 0] },
};
/** The faces in the order region names list them. */
const FACE_ORDER: readonly ViewCubeFace[] = ['top', 'bottom', 'front', 'back', 'left', 'right'];

/** The faces `direction` from the cube's center leans toward, in the order region names list them. */
const facesAlong = (direction: Vec3): ViewCubeFace[] => FACE_ORDER.filter(face => dot3d(FACE_FRAMES[face].normal, direction) > 0.5);

/** The region in `direction` from the cube's center: named by the faces it leans toward. */
const regionAlong = (direction: Vec3): ViewCubeRegion => facesAlong(direction).join('-') as ViewCubeRegion;

/** A region's direction from the cube's center to its visual center: each component -1, 0 or 1. */
interface RegionFrame {
  region: ViewCubeRegion;
  faces: ViewCubeFace[];
  direction: Vec3;
}

const UNIT_STEPS = [-1, 0, 1];
const REGION_FRAMES: readonly RegionFrame[] = UNIT_STEPS
  .flatMap(x => UNIT_STEPS.flatMap(y => UNIT_STEPS.map((z): Vec3 => [x, y, z])))
  .filter(direction => direction.some(component => component !== 0))
  .map(direction => ({ region: regionAlong(direction), faces: facesAlong(direction), direction }));
const REGION_BY_NAME = new Map(REGION_FRAMES.map(frame => [frame.region, frame]));

/** The camera's screen axes in cube terms: right, up, and toward the viewer; the camera's own look-at basis. */
interface ViewBasis {
  right: Vec3;
  up: Vec3;
  toward: Vec3;
}

function viewBasis({ azimuth, elevation }: CameraAngle): ViewBasis {
  const sinAzimuth = Math.sin(azimuth), cosAzimuth = Math.cos(azimuth), sinElevation = Math.sin(elevation), cosElevation = Math.cos(elevation);
  return {
    right: [cosAzimuth, 0, -sinAzimuth],
    up: [-sinElevation * sinAzimuth, cosElevation, -sinElevation * cosAzimuth],
    toward: [cosElevation * sinAzimuth, sinElevation, cosElevation * cosAzimuth],
  };
}

/** The eye, in cube terms. */
const eyeOf = (basis: ViewBasis): Vec3 => scale(basis.toward, EYE_DISTANCE);

/** Whether the eye stands on the outward side of the face's plane, half a side out from the center. */
const faceShows = (face: ViewCubeFace, basis: ViewBasis): boolean => dot3d(FACE_FRAMES[face].normal, eyeOf(basis)) - 0.5 > LEAST_FACING;

/** A point on a face of the unit cube, at (u, v) in its unit square. */
const facePoint = ({ normal, right, up }: FaceFrame, u: number, v: number): Vec3 => add(add(scale(normal, 0.5), scale(right, u - 0.5)), scale(up, 0.5 - v));

/** A face's four corners on the unit cube, clockwise as seen. */
const faceCorners = (frame: FaceFrame): Vec3[] => [facePoint(frame, 0, 0), facePoint(frame, 1, 0), facePoint(frame, 1, 1), facePoint(frame, 0, 1)];

/** The span of a face's unit square a region covers along one of its axes, by which way the region leans along it. */
const bandSpan = (lean: number): [number, number] => lean > 0 ? [1 - BAND, 1] : lean < 0 ? [0, BAND] : [BAND, 1 - BAND];

/** The part of the region in `direction` on the face of `frame`, as its corners on the unit cube, clockwise as seen. */
function regionPiece(direction: Vec3, frame: FaceFrame): Vec3[] {
  const [u0, u1] = bandSpan(dot3d(direction, frame.right));
  const [v0, v1] = bandSpan(-dot3d(direction, frame.up));
  return [facePoint(frame, u0, v0), facePoint(frame, u1, v0), facePoint(frame, u1, v1), facePoint(frame, u0, v1)];
}

const pointKey = (point: Vec3): string => point.map(coordinate => Math.round(coordinate * 1e6)).join(',');

/**
 * The outline of pieces that meet along whole sides, all wound the same way:
 * each side two pieces share runs both ways and drops out, and the sides left
 * chain into one loop.
 */
function unionOutline(pieces: Vec3[][]): Vec3[] {
  const sides = pieces.flatMap(piece => piece.map((point, index): [Vec3, Vec3] => [point, piece[(index + 1) % piece.length]]));
  const sideKeys = new Set(sides.map(([from, to]) => `${pointKey(from)}>${pointKey(to)}`));
  const boundary = sides.filter(([from, to]) => !sideKeys.has(`${pointKey(to)}>${pointKey(from)}`));
  const next = new Map(boundary.map(([from, to]) => [pointKey(from), to]));
  const outline = [boundary[0][0]];
  let point = next.get(pointKey(outline[0]))!;
  while (pointKey(point) !== pointKey(outline[0]) && outline.length < boundary.length) {
    outline.push(point);
    point = next.get(pointKey(point))!;
  }
  return outline;
}

/**
 * The cube's side, the same under a mouse and a finger and on every screen:
 * at the opening angle it stands the six faces' neighboring centers a touch
 * target apart; the narrower edges and corners take a touch at the nearest
 * region center, and a drag reaches every angle in between.
 */
const CUBE_SIDE = 64;

/** A button of `radius` about `center`, with its icon and highlight. */
function buttonShape(button: ViewCubeButton, center: Point, radius: number): ViewCubeButtonShape {
  const outline = Array.from({ length: BUTTON_OUTLINE_SEGMENTS }, (_, index) => {
    const turn = (2 * Math.PI * index) / BUTTON_OUTLINE_SEGMENTS;
    return { x: center.x + radius * Math.cos(turn), y: center.y + radius * Math.sin(turn) };
  });
  const box = { x: center.x - radius, y: center.y - radius, width: 2 * radius, height: 2 * radius };
  return { button, icon: BUTTON_ICONS[button], center, radius, box, outline };
}

/**
 * How far each button's center stands from the cube's center along both
 * axes, the buttons sitting at the corners of a square: the least that
 * clears its disc from the circle the cube turns in, and under touch its
 * whole touch target square too, each by the gap, so a touch that takes a
 * button first never takes one meant for the cube.
 */
function buttonOffset(inputKind: InputKind, cubeRadius: number, radius: number): number {
  const clearingDisc = (cubeRadius + radius + BUTTON_GAP) / Math.SQRT2;
  const clearingSquare = inputKind === 'touch' ? TOUCH_TARGET_REACH + (cubeRadius + BUTTON_GAP) / Math.SQRT2 : 0;
  return Math.max(clearingDisc, clearingSquare);
}

/**
 * Where the cube's box and the buttons sit in the area, and how big the area
 * is: the buttons at three corners of a square centered on the cube (spin
 * left top left, spin right top right, home bottom right), `buttonOffset`
 * out each way, and the area the square's bounds, with room for half of
 * every outline's stroke and a pixel past it for its antialiased edge, so
 * none is clipped at the area's edge.
 */
function areaLayout(inputKind: InputKind): Pick<ViewCubeLayout, 'width' | 'height' | 'side' | 'cubeBox' | 'buttons'> {
  const side = CUBE_SIDE;
  const boxSide = 2 * side * SCREEN_REACH + 2 * VIEW_CUBE_STYLE.strokeWidth;
  const cubeRadius = boxSide / 2, radius = BUTTON_SIZE[inputKind] / 2;
  const offset = buttonOffset(inputKind, cubeRadius, radius);
  const halfSide = Math.max(cubeRadius, offset + radius + VIEW_CUBE_STYLE.strokeWidth / 2) + EDGE_PIXEL;
  const button = (name: ViewCubeButton, across: number, down: number): ViewCubeButtonShape =>
    buttonShape(name, { x: halfSide + across * offset, y: halfSide + down * offset }, radius);
  return {
    width: 2 * halfSide,
    height: 2 * halfSide,
    side,
    cubeBox: { x: halfSide - cubeRadius, y: halfSide - cubeRadius, width: boxSide, height: boxSide },
    buttons: { 'turn-left': button('turn-left', -1, -1), 'turn-right': button('turn-right', 1, -1), home: button('home', 1, 1) },
  };
}

/** The perspective projection onto the cube area, and its rate of change along a direction at a point. */
interface CubeProjection {
  project(point: Vec3): Point;
  along(point: Vec3, direction: Vec3): Point;
}

/** Seen from the eye, keeping the scale on the plane through the cube's center at `side` per cube side. */
function cubeProjection(basis: ViewBasis, center: Point, side: number): CubeProjection {
  const reach = (point: Vec3): number => EYE_DISTANCE - dot3d(point, basis.toward);
  return {
    project: point => {
      const scaleThere = (side * EYE_DISTANCE) / reach(point);
      return { x: center.x + scaleThere * dot3d(point, basis.right), y: center.y - scaleThere * dot3d(point, basis.up) };
    },
    along: (point, direction) => {
      const distance = reach(point), approach = dot3d(direction, basis.toward), scaleThere = (side * EYE_DISTANCE) / distance ** 2;
      return {
        x: scaleThere * (dot3d(direction, basis.right) * distance + dot3d(point, basis.right) * approach),
        y: -scaleThere * (dot3d(direction, basis.up) * distance + dot3d(point, basis.up) * approach),
      };
    },
  };
}

/** The label's affine map for a face: the projection's own at the face's middle, (0.5, 0.5) landing exactly there. */
function labelMatrix(frame: FaceFrame, projection: CubeProjection): ViewCubeMatrix {
  const middle = scale(frame.normal, 0.5);
  const center = projection.project(middle);
  const acrossU = projection.along(middle, frame.right), acrossV = projection.along(middle, scale(frame.up, -1));
  return [acrossU.x, acrossU.y, acrossV.x, acrossV.y, center.x - (acrossU.x + acrossV.x) / 2, center.y - (acrossU.y + acrossV.y) / 2];
}

/** The cube seen from the camera's `angle` (each frame's `OverlayFrame.angle`), sized for `inputKind`. */
export function viewCubeLayout(angle: CameraAngle, inputKind: InputKind): ViewCubeLayout {
  const area = areaLayout(inputKind);
  const basis = viewBasis(angle);
  const eye = eyeOf(basis);
  const projection = cubeProjection(basis, { x: area.cubeBox.x + area.cubeBox.width / 2, y: area.cubeBox.y + area.cubeBox.height / 2 }, area.side);
  const shownFaces = FACE_ORDER.filter(face => faceShows(face, basis));
  const faces = shownFaces.map((face): ViewCubeFaceShape => {
    const frame = FACE_FRAMES[face];
    const viewNormal: Vec3 = [dot3d(frame.normal, basis.right), dot3d(frame.normal, basis.up), dot3d(frame.normal, basis.toward)];
    const [x, y, z] = sub(eye, scale(frame.normal, 0.5));
    return {
      face,
      label: frame.label,
      polygon: faceCorners(frame).map(projection.project),
      matrix: labelMatrix(frame, projection),
      fill: hexFromRgb(scale(PALETTE.wall, sceneLightAt(viewNormal))),
      eyeDistance: Math.hypot(x, y, z),
    };
  }).sort((farther, nearer) => nearer.eyeDistance - farther.eyeDistance);
  const regions = REGION_FRAMES
    .filter(frame => frame.faces.some(face => shownFaces.includes(face)))
    .map((frame): ViewCubeRegionShape => ({
      region: frame.region,
      outline: unionOutline(frame.faces.filter(face => shownFaces.includes(face)).map(face => regionPiece(frame.direction, FACE_FRAMES[face]))).map(
        projection.project,
      ),
      center: projection.project(scale(frame.direction, 0.5)),
    }));
  const silhouette = unionOutline(shownFaces.map(face => faceCorners(FACE_FRAMES[face]))).map(projection.project);
  return { inputKind, ...area, faces, regions, silhouette };
}

const contains = (polygon: readonly Point[], { x, y }: Point): boolean => evenOddContains([polygon.map(point => [point.x, point.y] as const)], x, y);

/** The button whose circle `point` is in, if any. */
const buttonAt = ({ buttons }: ViewCubeLayout, point: Point): ViewCubeButton | null =>
  Object.values(buttons).find(({ center, radius }) => Math.hypot(point.x - center.x, point.y - center.y) <= radius)?.button ?? null;

/** A mouse hits exactly: a button's circle, else the region drawn under it. */
const pointerHit = (layout: ViewCubeLayout, point: Point): ViewCubeHit | null =>
  buttonAt(layout, point) ?? layout.regions.find(({ outline }) => contains(outline, point))?.region ?? null;

/**
 * A touch in a button's touch target square takes that button, before the
 * cube. Otherwise it takes the region whose center is nearest, anywhere on
 * the cube, so the narrow edges and corners stay easy to hit, or, off the
 * cube, the nearest region whose touch target square it lands in.
 */
function touchHit(layout: ViewCubeLayout, point: Point): ViewCubeHit | null {
  const buttons = Object.values(layout.buttons).map(({ button, center }) => ({ hit: button as ViewCubeHit, center }));
  const regions = layout.regions.map(({ region, center }) => ({ hit: region as ViewCubeHit, center }));
  const pressedButtons = buttons.filter(({ center }) => withinTouchTarget(point, center));
  if (pressedButtons.length > 0) return nearestTarget(pressedButtons, point);
  return nearestTarget(contains(layout.silhouette, point) ? regions : regions.filter(({ center }) => withinTouchTarget(point, center)), point);
}

/** The target whose center is nearest `point`; null with none. */
function nearestTarget(targets: readonly { hit: ViewCubeHit; center: Point }[], point: Point): ViewCubeHit | null {
  let nearest: ViewCubeHit | null = null, nearestDistance = Infinity;
  for (const { hit, center } of targets) {
    const distance = Math.hypot(point.x - center.x, point.y - center.y);
    if (distance < nearestDistance) [nearest, nearestDistance] = [hit, distance];
  }
  return nearest;
}

/** What a press at `point` (in the cube area's coordinates) takes: a region, a button, or null off them. */
export const viewCubeRegionAt = (layout: ViewCubeLayout, point: Point): ViewCubeHit | null =>
  layout.inputKind === 'touch' ? touchHit(layout, point) : pointerHit(layout, point);

/** The outline to light for `hit` on hover or press; null for a region that does not show. */
export const viewCubeOutline = (layout: ViewCubeLayout, hit: ViewCubeHit): Point[] | null =>
  isViewCubeButton(hit) ? layout.buttons[hit].outline : layout.regions.find(shape => shape.region === hit)?.outline ?? null;

/**
 * The camera angle that looks from `region`, in the plan's terms: a side
 * face from level with its middle, Top straight down (the plan view, the
 * front edge at the bottom) and Bottom straight up, an edge from halfway
 * between its two faces, and a corner from 45° around and 45° up (or down).
 */
export function viewCubeAngle(region: ViewCubeRegion): CameraAngle {
  const [x, y, z] = REGION_BY_NAME.get(region)!.direction;
  const level = x !== 0 || z !== 0;
  const height = !level ? VIEW_ELEVATION_LIMIT : x !== 0 && z !== 0 ? CORNER_ELEVATION : EDGE_ELEVATION;
  return { azimuth: level ? Math.atan2(x, z) : 0, elevation: y * height };
}
